import React from 'react';
import {Button} from '@material-ui/core';
import {VerticalAlignBottom} from '@material-ui/icons';
import ReactGA from 'react-ga4';
import {makeStyles} from '@material-ui/core/styles';
import * as XLSX from 'xlsx';
import usePersistentAuthStore from '../../store/persistentAuthStore';

// Import the number formatter from dataHelpers
const {nf} = require('./stackedReport/helpers/dataHelpers');

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1),
  },
}));

interface DataExportProps {
  dataType: string;
  publisherStats: any[];
  period: {
    startDate: string;
    endDate: string;
  };
}

const DataExport: React.FC<DataExportProps> = ({dataType, publisherStats, period}) => {
  const classes = useStyles();
  const {auth} = usePersistentAuthStore();

  // Export column configuration
  const exportConfig = [
    {dataKey: 'date', columnName: 'Date'},
    {dataKey: 'Rev', columnName: 'Revenue'},
    {dataKey: 'All Imp', columnName: 'Requests'},
    {dataKey: 'Paid Imp', columnName: 'Impressions'},
    {dataKey: 'eCPM', columnName: 'eCPM'},
    {dataKey: 'fillRate', columnName: 'Fill Rate'},
    {dataKey: 'Rev Refresh', columnName: 'Refresh Revenue', addIf: 'refresh_enabled'},
    {dataKey: 'refreshRate', columnName: 'Smart Refresh Rate', addIf: 'refresh_enabled'},
    {dataKey: 'Viewability', columnName: 'Viewability', addIf: 'viewability_enabled'},
    {dataKey: 'rCPM', columnName: 'rCPM', addIf: 'rCPM_enabled'},
    {dataKey: 'CTR', columnName: 'CTR', addIf: 'ctr_enabled'},
  ];

  const exportXLS = (): void => {
    ReactGA.event({
      category: 'Export to XLS',
      action: 'click',
      label: dataType,
    });

    try {
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();

      // Convert data to worksheet format using config
      const wsData = publisherStats.map(day => {
        const row: Record<string, string | number> = {};

        exportConfig.forEach(({dataKey, columnName, addIf}) => {
          // Skip column if addIf condition is not met
          if (addIf && auth?.[addIf] !== 1) {
            return;
          }

          // Add column value (use raw value for date, formatted value for others)
          row[columnName] = dataKey === 'date' ? day[dataKey] : nf(day[dataKey]) || 0;
        });

        return row;
      });

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(wsData);

      // Set column widths dynamically for all columns
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      const colWidths = [];

      for (let col = range.s.c; col <= range.e.c; col++) {
        colWidths.push({wch: 25});
      }

      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, `${dataType}_data`);

      // Generate filename
      const fileName = `${auth?.username}_setupad_daily_${dataType}_${period.startDate}_${period.endDate}.xlsx`;

      // Download the file
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  return (
    <Button
      size={'small'}
      variant={'outlined'}
      style={{marginTop: 20}}
      className={classes.button}
      endIcon={<VerticalAlignBottom />}
      onClick={exportXLS}
    >
      Export to XLS
    </Button>
  );
};

export default DataExport;
